import { AxiosRequestConfig } from "axios";
import { api, prefix } from "@/api";
const namespace = "switchboard";

export const getQueue = (config: AxiosRequestConfig = {}) => {
  return api<any>({
    url: `${prefix}/${namespace}/queue`,
    method: "GET",
    ...config,
  });
};

// LEGACY (GAIA-2643): aggregati della tabella Stats del fornitore, usato solo
// da QueueHistoryDialog. Per la pagina KPI/Stats usare getStatsServiceSummary/getStatsCalls.
export const getStats = (config: AxiosRequestConfig = {}) => {
  return api<any>({
    url: `${prefix}/${namespace}/stats`,
    method: "GET",
    ...config,
  });
};

export const getExtensions = (config: AxiosRequestConfig = {}) => {
  return api<any>({
    url: `${prefix}/${namespace}/extension`,
    method: "GET",
    ...config,
  });
};

// Pausa un interno
export const pauseExtension = (
  extension: string,
  reason: string = "Pausa Utente",
  interfaceName?: string,
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/${namespace}/extension/${extension}/pause`,
    method: "PATCH",
    params: { extension, action: "pause" },
    data: { reason, paused: true, ...(interfaceName ? { interfaceName } : {}) },
    ...config,
  });
};

// Rimuove la pausa da un interno
export const unpauseExtension = (
  extension: string,
  interfaceName?: string,
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/${namespace}/extension/${extension}/unpause`,
    method: "PATCH",
    params: { extension, action: "unpause" },
    data: { paused: false, ...(interfaceName ? { interfaceName } : {}) },
    ...config,
  });
};

// Sposta un interno da una coda all'altra
export const moveExtension = (
  extension: string,
  fromQueue: string,
  toQueue: string,
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/${namespace}/extension/${extension}/move`,
    method: "PATCH",
    params: { extension, action: "move" },
    data: { fromQueue, toQueue },
    ...config,
  });
};

// Aggiunge un interno a una coda
export const addExtensionToQueue = (
  extension: string,
  queue: string,
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/${namespace}/extension/${extension}/add-to-queue`,
    method: "PATCH",
    params: { extension, action: "add-to-queue" },
    data: { queue },
    ...config,
  });
};

// Rimuove un interno da una coda
export const removeExtensionFromQueue = (
  extension: string,
  queue: string,
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/${namespace}/extension/${extension}/remove-from-queue`,
    method: "PATCH",
    params: { extension, action: "remove-from-queue" },
    data: { queue },
    ...config,
  });
};

// Aggiunge un numero alla blacklist del centralino.
// Il numero DEVE essere già in formato E.164 (+39…) — vedi normalizeItalianE164.
// Va nel body (non in URL/query) per non esporre il numero in log/URL.
export const addToBlacklist = (
  number: string,
  description?: string,
  config: AxiosRequestConfig = {}
) => {
  return api<any>({
    url: `${prefix}/${namespace}/blacklist`,
    method: "POST",
    data: { number, ...(description ? { description } : {}) },
    ...config,
  });
};

// Rimuove un numero dalla blacklist del centralino (E.164, nel body).
export const removeFromBlacklist = (number: string, config: AxiosRequestConfig = {}) => {
  return api<any>({
    url: `${prefix}/${namespace}/blacklist`,
    method: "DELETE",
    data: { number },
    ...config,
  });
};

// Recupera informazioni sulla chiamata attiva
export const getExtensionCall = (extension: string, config: AxiosRequestConfig = {}) => {
  return api<any>({
    url: `${prefix}/${namespace}/extension/${extension}/call`,
    method: "GET",
    ...config,
  });
};

// Chiude la chiamata attiva di un interno (supervisor/admin)
export const hangupExtensionCall = (extension: string, config: AxiosRequestConfig = {}) => {
  return api<any>({
    url: `${prefix}/${namespace}/extension/${extension}/hangup`,
    method: "PATCH",
    ...config,
  });
};

// --- GAIA-2643: report stats da eventi centralino ---
// Convenzione: suffisso Dto = shape wire del BE (passa dai mapper di
// switchboardStatsMapping prima di arrivare alla UI; i view-model sono senza suffisso).

export type QueueCallOutcome = "answered" | "abandoned" | "timeout" | "open" | "stale";

export type QueueCallSource = "live" | "legacy";

/**
 * Tentativi di squillo per interno su una chiamata (GAIA-2731). `attempts` conta i
 * cicli di squillo, `noAnswer` quanti si sono chiusi senza risposta.
 */
export interface QueueRingSummaryDto {
  extension: string;
  attempts: number;
  noAnswer: number;
}

export interface QueueCallRecordDto {
  uniqueid: string;
  queue: string;
  callerId: string | null;
  enteredAt: string;
  answeredAt: string | null;
  endedAt: string | null;
  agentExtension: string | null;
  holdTimeSec: number | null;
  talkTimeSec: number | null;
  outcome: QueueCallOutcome;
  positionAtJoin: number | null;
  positionAtAbandon: number | null;
  /**
   * Opzionale sul tipo, non sul contratto: il BE lo manda sempre da GAIA-2731, ma
   * durante il rollout il FE puo' girare contro una versione che ancora non lo ha.
   */
  ringSummary?: QueueRingSummaryDto[];
  /**
   * Servizio della chiamata (GAIA-2955): DID composto ed etichetta della route.
   * Opzionali sul tipo per la stessa ragione di ringSummary (rollout).
   */
  did?: string | null;
  serviceLabel?: string | null;
}

export interface StatsSummaryParams {
  startDate: string; // ISO — il BE espande a giorni interi italiani (Europe/Rome)
  endDate: string; // ISO
  queue?: string;
}

export interface StatsCallsParams extends StatsSummaryParams {
  outcome?: QueueCallOutcome;
  extension?: string;
  skip?: number;
  take?: number;
}

export interface StatsCallsResponse {
  data: QueueCallRecordDto[];
  count: number; // totale pre-paginazione
}

// NB: l'interceptor api (onResponseExtractData) risolve già col body della
// risposta: il valore atteso è direttamente {data, count}, senza involucro
// Axios. Cast esplicito per allineare il tipo al runtime (pattern call-log.ts).

// Dettaglio per-chiamata dei record di coda (dal go-live in poi).
export const getStatsCalls = async (
  params: StatsCallsParams,
  config: AxiosRequestConfig = {}
): Promise<StatsCallsResponse> =>
  (await api<StatsCallsResponse>({
    url: `${prefix}/${namespace}/stats/calls`,
    method: "GET",
    params,
    ...config,
  })) as unknown as StatsCallsResponse;

// --- GAIA-2955: KPI SLA per servizio ---

/** Esiti della vista Dettaglio inbound: quelli di coda più le chiamate mai entrate in coda. */
export type StatsCallOutcome = QueueCallOutcome | "notQueued";

export type ServiceSummaryGroupBy = "day" | "period";

export interface ServiceSummaryRowDto {
  date: string | null; // YYYY-MM-DD giorno italiano; null con groupBy=period
  queue: string | null; // day: coda del gruppo; period: coda dedotta; null = nessuna
  did: string | null; // null = "Non identificato"
  serviceLabel: string | null;
  source: QueueCallSource;
  received: number;
  notQueued: number | null; // null sulle righe legacy
  queued: number; // answered + abandoned + abandonedShort + timeout
  answered: number;
  abandoned: number; // attesa >= 10s o non misurata
  abandonedShort: number | null; // attesa < 10s; null sulle righe legacy
  timeout: number | null;
  unknown: number | null; // open + stale
  sla60: number;
  avgWaitSec: number | null;
  avgAbandonWaitSec: number | null;
  avgTalkSec: number | null;
}

export interface ServiceSummaryParams extends StatsSummaryParams {
  groupBy: ServiceSummaryGroupBy;
}

export interface ServiceSummaryResponse {
  data: ServiceSummaryRowDto[];
  count: number;
}

export interface NotQueuedCallDto {
  uniqueid: string;
  did: string;
  serviceLabel: string | null;
  queue: string | null; // coda dedotta del servizio
  callerId: string | null;
  receivedAt: string;
  endedAt: string | null;
}

export type NotQueuedCallsParams = StatsSummaryParams;

export interface NotQueuedCallsResponse {
  data: NotQueuedCallDto[];
  count: number;
}

export const getStatsServiceSummary = async (
  params: ServiceSummaryParams,
  config: AxiosRequestConfig = {}
): Promise<ServiceSummaryResponse> =>
  (await api<ServiceSummaryResponse>({
    url: `${prefix}/${namespace}/stats/service-summary`,
    method: "GET",
    params,
    ...config,
  })) as unknown as ServiceSummaryResponse;

export const getStatsNotQueued = async (
  params: NotQueuedCallsParams,
  config: AxiosRequestConfig = {}
): Promise<NotQueuedCallsResponse> =>
  (await api<NotQueuedCallsResponse>({
    url: `${prefix}/${namespace}/stats/not-queued`,
    method: "GET",
    params,
    ...config,
  })) as unknown as NotQueuedCallsResponse;

// ---------------------------------------------------------------------------
// Trasferimento cieco della chiamata attiva (GAIA-2776)
// ---------------------------------------------------------------------------

export type CallTransferOutcome =
  | "completed"
  | "consulting"
  | "noanswer"
  | "busy"
  | "failed";

/** Cieco: passa e basta. Consultativo: parla prima col collega (GAIA-2777). */
export type CallTransferMode = "blind" | "consultative";

export interface CallTransferResponse {
  outcome: CallTransferOutcome;
  toExtension: string;
}

/**
 * Quanto la barra aspetta l'esito del trasferimento.
 *
 * L'endpoint è long-running di proposito: risponde quando l'esito è noto, non
 * quando l'azione è partita. Questo timeout DEVE stare sopra la guardia di
 * `service-switchboard` (~25s), che a sua volta sta sopra il tempo di squillo
 * del destinatario nel dialplan (~20s). Se l'ordine si inverte, la barra molla
 * mentre il centralino sta ancora decidendo e l'operatore vede un errore su un
 * trasferimento che invece è riuscito.
 */
export const CALL_TRANSFER_TIMEOUT_MS = 30_000;

/**
 * Devia la chiamata attiva dell'operatore verso un altro interno.
 *
 * La barra non manda un REFER: con un trasferimento cieco puro la sua gamba si
 * chiuderebbe prima di sapere se il destinatario risponde, e non resterebbe
 * nulla a cui far rientrare la chiamata. Il controllo sta sul centralino, che
 * tiene viva la gamba durante lo squillo e ri-bridgia in caso di mancata
 * risposta.
 *
 * L'esito qui restituito serve a dare il MOTIVO all'avviso mostrato
 * all'operatore, non a decidere cosa è successo: quello lo dice lo stato della
 * sessione SIP, che è l'unica sorgente che non può mentire.
 */
export const transferCall = async (
  fromExtension: string,
  toExtension: string,
  mode: CallTransferMode = "blind",
  config: AxiosRequestConfig = {}
): Promise<CallTransferResponse> => {
  if (!fromExtension) throw new Error("transferCall: fromExtension is required");
  if (!toExtension) throw new Error("transferCall: toExtension is required");
  if (fromExtension === toExtension) {
    throw new Error("transferCall: non si trasferisce una chiamata a sé stessi");
  }

  return (await api<CallTransferResponse>({
    url: `${prefix}/${namespace}/transfer`,
    method: "POST",
    data: { fromExtension, toExtension, mode },
    timeout: CALL_TRANSFER_TIMEOUT_MS,
    ...config,
  })) as unknown as CallTransferResponse;
};

/**
 * Consultativo (GAIA-2777): passa il cliente al collega con cui si sta parlando.
 * Il completamento e' la chiusura della gamba dell'operatore lato centralino.
 */
export const completeTransfer = async (
  fromExtension: string,
  config: AxiosRequestConfig = {}
): Promise<CallTransferResponse> => {
  if (!fromExtension) throw new Error("completeTransfer: fromExtension is required");

  return (await api<CallTransferResponse>({
    url: `${prefix}/${namespace}/transfer/${fromExtension}/complete`,
    method: "POST",
    ...config,
  })) as unknown as CallTransferResponse;
};

/**
 * Consultativo (GAIA-2777): rinuncia al passaggio e riprendi il cliente.
 * `CancelAtxfer` ri-bridgia lato centralino: non si ricompone niente da qui.
 */
export const cancelTransfer = async (
  fromExtension: string,
  config: AxiosRequestConfig = {}
): Promise<CallTransferResponse> => {
  if (!fromExtension) throw new Error("cancelTransfer: fromExtension is required");

  return (await api<CallTransferResponse>({
    url: `${prefix}/${namespace}/transfer/${fromExtension}/cancel`,
    method: "POST",
    ...config,
  })) as unknown as CallTransferResponse;
};
